import Icons from "../Icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { Nav, Row, Col } from "react-bootstrap"
import TenantContext from "../tenancy/TenantContext"
import { useContext } from "react"
import { LinkContainer } from "react-router-bootstrap"
import {
	useHasNhsEnglandExtract,
	useIsTenantShutdown,
	useTenantSubDomain
} from "../tenancy/TenantHooks"
import useIsZone from "../../hooks/useIsZone"

const LeftNav = ({
	showNav,
	toggleNavVisibility
}: {
	showNav: boolean
	toggleNavVisibility: () => void
}) => {
	const { isDevelopment, isTest, isProduction } = useIsZone()
	const tenantSubDomain = useTenantSubDomain()
	const isTenantShutdown = useIsTenantShutdown()
	const tenantContext = useContext(TenantContext)
	const tenantEnvironment = tenantContext.tenant?.environment
	const hasNhsNumberEnabled =
		tenantContext?.tenant?.configuration.features.nhs.englandAndWales
	const isSsoEnabled =
		tenantEnvironment?.professionalSsoSettings.isTestSsoEndpointEnabled
	const hasNhsEnglandExtract = useHasNhsEnglandExtract()

	return (
		<nav className="d-none d-md-block bg-light sidebar text-left">
			<Row>
				<Col
					className="sidebar-sticky"
					style={{ display: showNav ? "block" : "none" }}
				>
					<Nav
						activeKey={window.location.pathname}
						className="nav flex-column"
						as="ul"
					>
						<LinkContainer className="nav-link" to="">
							<Nav.Item as="li" className="nav-item">
								<FontAwesomeIcon icon={Icons.TachometerAlt} />
								Dashboard{" "}
								<span className="sr-only">(current)</span>
							</Nav.Item>
						</LinkContainer>
					</Nav>

					<h6 className="sidebar-heading px-3 mt-1 text-muted">
						Professionals
					</h6>
					<Nav
						activeKey={window.location.pathname}
						className="nav flex-column mb-2"
						as="ul"
					>
						<LinkContainer
							className="nav-link"
							to="professionals/search"
						>
							<Nav.Item as="li" className="nav-item">
								<FontAwesomeIcon icon={Icons.UserMd} />
								Manage Professionals
							</Nav.Item>
						</LinkContainer>
						<LinkContainer
							className="nav-link"
							to="professionals/add"
						>
							<Nav.Item as="li" className="nav-item">
								<FontAwesomeIcon icon={Icons.UserPlus} />
								Add Professional
							</Nav.Item>
						</LinkContainer>
						<LinkContainer
							className="nav-link"
							to="professionals/bulkadd"
						>
							<Nav.Item as="li" className="nav-item">
								<FontAwesomeIcon icon={Icons.Upload} />
								Bulk Add
							</Nav.Item>
						</LinkContainer>
						<LinkContainer
							className="nav-link"
							to="professionals/sharing/bulkshare"
						>
							<Nav.Item as="li" className="nav-item">
								<FontAwesomeIcon icon={Icons.Share} />
								Bulk Share
							</Nav.Item>
						</LinkContainer>
						<LinkContainer
							className="nav-link"
							to="professionals/sharing/bulkadd"
						>
							<Nav.Item as="li" className="nav-item">
								<FontAwesomeIcon icon={Icons.ShareAlt} />
								Share All
							</Nav.Item>
						</LinkContainer>
					</Nav>
					<h6 className="sidebar-heading px-3 mt-1 text-muted">
						Patients
					</h6>
					<Nav
						activeKey={window.location.pathname}
						className="nav flex-column mb-2"
						as="ul"
					>
						<LinkContainer
							className="nav-link"
							to="patients/search"
						>
							<Nav.Item as="li" className="nav-item">
								<FontAwesomeIcon icon={Icons.Users} />
								Manage Patients
							</Nav.Item>
						</LinkContainer>
						<LinkContainer
							className="nav-link"
							to="patients/issues"
						>
							<Nav.Item as="li" className="nav-item">
								<FontAwesomeIcon icon={Icons.Procedures} />
								Fix Assessment Issues
							</Nav.Item>
						</LinkContainer>
						{hasNhsNumberEnabled && (
							<LinkContainer
								className="nav-link"
								to="patients/nhsNumbers"
							>
								<Nav.Item as="li" className="nav-item">
									<FontAwesomeIcon
										icon={Icons.HospitalUser}
									/>
									Upload NHS Numbers
								</Nav.Item>
							</LinkContainer>
						)}
						<LinkContainer
							className="nav-link"
							to="patients/events"
						>
							<Nav.Item as="li" className="nav-item">
								<FontAwesomeIcon icon={Icons.ClipboardList} />
								Event Log
							</Nav.Item>
						</LinkContainer>
						{!isProduction && (
							<LinkContainer className="nav-link" to="fakedata">
								<Nav.Item as="li" className="nav-item">
									<FontAwesomeIcon icon={Icons.UserNinja} />
									Fake Data
								</Nav.Item>
							</LinkContainer>
						)}
					</Nav>
					<h6 className="sidebar-heading px-3 mt-1 text-muted">
						Extracts
					</h6>
					<Nav
						activeKey={window.location.pathname}
						className="nav flex-column mb-2"
						as="ul"
					>
						<LinkContainer
							className="nav-link"
							to="extracts/consultant-emails"
						>
							<Nav.Item as="li" className="nav-item">
								<FontAwesomeIcon icon={Icons.FileExport} />
								Consultant Emails
							</Nav.Item>
						</LinkContainer>
						{hasNhsEnglandExtract && (
							<LinkContainer
								className="nav-link"
								to="extracts/nhsengland"
							>
								<Nav.Item as="li" className="nav-item">
									<FontAwesomeIcon icon={Icons.FileExport} />
									NHS England
								</Nav.Item>
							</LinkContainer>
						)}
						<LinkContainer className="nav-link" to="extracts/phin">
							<Nav.Item as="li" className="nav-item">
								<FontAwesomeIcon icon={Icons.FileExport} />
								PHIN
							</Nav.Item>
						</LinkContainer>
						<LinkContainer
							className="nav-link"
							to="extracts/tenant"
						>
							<Nav.Item as="li" className="nav-item">
								<FontAwesomeIcon icon={Icons.FileExport} />
								Tenant Specific
							</Nav.Item>
						</LinkContainer>
					</Nav>
					<h6 className="sidebar-heading px-3 mt-1 text-muted">
						Integrations
					</h6>
					<Nav
						activeKey={window.location.pathname}
						className="nav flex-column mb-2"
						as="ul"
					>
						<LinkContainer
							className="nav-link"
							to="integrations/bridge-records"
						>
							<Nav.Item as="li" className="nav-item">
								<FontAwesomeIcon icon={Icons.FileImport} />
								Bridge Records
							</Nav.Item>
						</LinkContainer>
						<LinkContainer
							className="nav-link"
							to="integrations/unpack-transform-merge"
						>
							<Nav.Item as="li" className="nav-item">
								<FontAwesomeIcon
									icon={Icons.BriefcaseArrowRight}
								/>
								Unpack, Transform & Merge
							</Nav.Item>
						</LinkContainer>
						{tenantSubDomain === "genesiscare" && (
							<LinkContainer
								className="nav-link"
								to="integrations/genesis-care-bridge-data"
							>
								<Nav.Item as="li" className="nav-item">
									<FontAwesomeIcon
										icon={Icons.BriefcaseArrowRight}
									/>
									Genesis Care Bridge Data
								</Nav.Item>
							</LinkContainer>
						)}
						{(isDevelopment || isTest) &&
							tenantSubDomain === "genesiscare" && (
								<LinkContainer
									className="nav-link"
									to="integrations/genesis-care-test-api"
								>
									<Nav.Item as="li" className="nav-item">
										<FontAwesomeIcon
											icon={Icons.BriefcaseArrowRight}
										/>
										Genesis Care Test API
									</Nav.Item>
								</LinkContainer>
							)}
						{tenantSubDomain === "spire" && (
							<LinkContainer
								className="nav-link"
								to="integrations/spire-bridge-data"
							>
								<Nav.Item as="li" className="nav-item">
									<FontAwesomeIcon
										icon={Icons.BriefcaseArrowRight}
									/>
									Spire Bridge Data
								</Nav.Item>
							</LinkContainer>
						)}
						{(isDevelopment || isTest) &&
							tenantSubDomain === "spire" && (
								<LinkContainer
									className="nav-link"
									to="integrations/spire-test-api"
								>
									<Nav.Item as="li" className="nav-item">
										<FontAwesomeIcon
											icon={Icons.BriefcaseArrowRight}
										/>
										Spire Test API
									</Nav.Item>
								</LinkContainer>
							)}
					</Nav>
					<h6 className="sidebar-heading px-3 mt-1 text-muted">
						Templates
					</h6>
					<Nav
						activeKey={window.location.pathname}
						className="nav flex-column mb-2"
						as="ul"
					>
						<LinkContainer
							className="nav-link"
							to="templates/emails/preview"
						>
							<Nav.Item as="li" className="nav-item">
								<FontAwesomeIcon
									icon={Icons.EnvelopeOpenText}
								/>
								Email Preview
							</Nav.Item>
						</LinkContainer>
						<LinkContainer
							className="nav-link"
							to="templates/sms/preview"
						>
							<Nav.Item as="li" className="nav-item">
								<FontAwesomeIcon icon={Icons.Sms} />
								SMS Preview
							</Nav.Item>
						</LinkContainer>
					</Nav>
					{isSsoEnabled && (
						<>
							<h6 className="sidebar-heading px-3 mt-1 text-muted">
								Client
							</h6>
							<Nav
								activeKey={window.location.pathname}
								className="nav flex-column mb-2"
								as="ul"
							>
								<LinkContainer
									className="nav-link"
									to="client/sso/professional"
								>
									<Nav.Item as="li" className="nav-item">
										<FontAwesomeIcon icon={Icons.Key} />
										Professional SSO
									</Nav.Item>
								</LinkContainer>
							</Nav>
						</>
					)}
					<h6 className="sidebar-heading px-3 mt-1 text-muted">
						Super User
					</h6>
					<Nav
						activeKey={window.location.pathname}
						className="nav flex-column mb-2"
						as="ul"
					>
						{!isProduction && (
							<LinkContainer className="nav-link" to="devtools">
								<Nav.Item as="li" className="nav-item">
									<FontAwesomeIcon icon={Icons.UserNinja} />
									Development Tools
								</Nav.Item>
							</LinkContainer>
						)}
						<LinkContainer className="nav-link" to="assessments">
							<Nav.Item as="li" className="nav-item">
								<FontAwesomeIcon icon={Icons.ListAlt} />
								Assessments
							</Nav.Item>
						</LinkContainer>
						<LinkContainer className="nav-link" to="states">
							<Nav.Item as="li" className="nav-item">
								<FontAwesomeIcon icon={Icons.ListAlt} />
								State Machines
							</Nav.Item>
						</LinkContainer>
						<LinkContainer className="nav-link" to="config-search">
							<Nav.Item as="li" className="nav-item">
								<FontAwesomeIcon icon={Icons.FolderSearch} />
								Config Search
							</Nav.Item>
						</LinkContainer>
						<LinkContainer className="nav-link" to="audit">
							<Nav.Item as="li" className="nav-item">
								<FontAwesomeIcon icon={Icons.ClipboardList} />
								Audit
							</Nav.Item>
						</LinkContainer>
						{isTenantShutdown && (
							<LinkContainer
								className="nav-link"
								to="decommission"
							>
								<Nav.Item as="li" className="nav-item">
									<FontAwesomeIcon icon={Icons.Power} />
									Decommission
								</Nav.Item>
							</LinkContainer>
						)}
					</Nav>
				</Col>
				<Col className="sidebar-hider" onClick={toggleNavVisibility}>
					<div className="row h-100">
						{showNav && (
							<FontAwesomeIcon
								className="my-auto"
								icon={Icons.ChevronLeft}
							/>
						)}
						{!showNav && (
							<FontAwesomeIcon
								className="my-auto"
								icon={Icons.ChevronRight}
							/>
						)}
					</div>
				</Col>
			</Row>
		</nav>
	)
}

export default LeftNav
